import { create } from 'zustand';
import type { LoginResponse, Permission } from '@shared/types';
import { ApiRequestError, api, setUnauthorizedHandler } from '../lib/api';

/**
 * 会话状态。
 *
 * 会话本身是 httpOnly Cookie，前端拿不到也不该拿到；这里只缓存
 * 「当前用户 + 其权限」，用于渲染导航与按钮的可用性。
 * 任何接口返回 401 都会把状态重置为匿名，避免界面停留在过期视图上。
 */

export type AuthStatus = 'loading' | 'anonymous' | 'authenticated';

interface AuthState {
  status: AuthStatus;
  user: LoginResponse['user'] | null;
  permissions: Permission[];
  mustChangePassword: boolean;
  /** 最近一次会话刷新失败的原因，用于登录页给出提示 */
  error: string | null;
  bootstrap: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  /** 管理员改动了自己的资料（由他人重置口令等）后强制回读一次 */
  refresh: () => Promise<void>;
  markAnonymous: () => void;
}

function applySession(response: LoginResponse) {
  return {
    status: 'authenticated' as AuthStatus,
    user: response.user,
    permissions: response.permissions,
    mustChangePassword: response.mustChangePassword,
    error: null,
  };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'loading',
  user: null,
  permissions: [],
  mustChangePassword: false,
  error: null,

  async bootstrap() {
    try {
      const session = await api.get<LoginResponse>('/api/auth/me');
      set(applySession(session));
    } catch (error) {
      if (error instanceof ApiRequestError && error.status >= 500) {
        set({ status: 'anonymous', error: error.message });
        return;
      }
      set({ status: 'anonymous', user: null, permissions: [], mustChangePassword: false });
    }
  },

  async login(email, password) {
    const session = await api.post<LoginResponse>('/api/auth/login', { email, password });
    set(applySession(session));
  },

  async logout() {
    try {
      await api.post('/api/auth/logout');
    } finally {
      set({ status: 'anonymous', user: null, permissions: [], mustChangePassword: false });
    }
  },

  async changePassword(currentPassword, newPassword) {
    const session = await api.post<LoginResponse>('/api/auth/password', {
      currentPassword,
      newPassword,
    });
    set(applySession(session));
  },

  async refresh() {
    if (get().status !== 'authenticated') return;
    const session = await api.get<LoginResponse>('/api/auth/me');
    set(applySession(session));
  },

  markAnonymous() {
    set({ status: 'anonymous', user: null, permissions: [], mustChangePassword: false });
  },
}));

/**
 * 全局 401 处理：注册到 API 客户端，任何请求遇到会话失效都会
 * 立刻把状态重置为匿名，界面随即回到登录页。
 */
export function registerUnauthorizedHandler(): () => void {
  setUnauthorizedHandler(() => {
    useAuthStore.getState().markAnonymous();
  });
  return () => setUnauthorizedHandler(null);
}
